import { Injectable } from '@angular/core';
import { PreviousPeriodHelper } from '../helpers/previous-period.helper';
import { PdfTronMapperHelper } from '../helpers/pdf-tron-mapper.helper';
import {
    FixedAssetScheduleRowFormatted,
    FixedAssetScheduleTableDataForPrint,
    Levels,
    RowTypes,
} from '@modeso/aumico-lib-annualreport-fe';
import {
    AumFinancialNumberPipe,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    EMPTY_STRING,
    NumberUtils,
} from '@modeso/aumico-lib-core-fe';
import { isFli, isGroup, isSubTotal } from '@modeso/aumico-lib-templates-fe';
import {
    AccountMappingTemplateGroupItem,
    FixedAssetsSchedule,
    FixedAssetsScheduleMovement,
    FixedAssetsScheduleReferenceType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { localizationKeys } from '../../../utils/localizationKeys';

const DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME = 'DIRECT_ASSIGNED_GROUPED_MOVEMENTS';

interface SubtotalInfo {
    name: string;
    isMarkedForRemoval: boolean;
}

@Injectable()
export class FixedAssetsScheduleSwissMapper {
    local = localizationKeys;

    private rowNumber = 0;

    constructor(
        private readonly aumFinancialNumberPipe: AumFinancialNumberPipe,
        private dateLocalePipe: DateLocalePipe,
    ) {}

    public mapFixedAssetsScheduleToFixedAssetsScheduleTableDataForPrint(
        fixedAssetsSchedule: FixedAssetsSchedule,
        mappingTemplate: IAccountMappingTemplate,
        openingDate: string,
        closingDate: string,
        hideDecimalPlaces: boolean,
        roundToThousands: boolean,
        hideEmptyLines: boolean,
        currency: string,
        predefinedLabels: Map<string, string>,
        nonBreakingHyphen: boolean,
        noPreviousData: boolean,
        currentPeriodStartDate: string,
    ): FixedAssetScheduleTableDataForPrint {
        const openingDateObj = new Date(openingDate);
        // Transform openingDate as is
        const openingDateForPreview = this.dateLocalePipe.transform(openingDateObj).toLocaleString();

        // Add 1 day to the openingDate
        const openingDatePlus1DayObj = new Date(openingDateObj);
        openingDatePlus1DayObj.setDate(openingDatePlus1DayObj.getDate() + 1);

        // Transform the new date (openingDate + 1 day)
        const openingDatePlus1DayForPreview = this.dateLocalePipe.transform(openingDatePlus1DayObj).toLocaleString();

        // Dates the opening columns.
        const periodStart = PreviousPeriodHelper.toDate(currentPeriodStartDate);
        const periodStartForPreview = noPreviousData
            ? periodStart == null
                ? ''
                : this.dateLocalePipe.transform(periodStart).toLocaleString()
            : openingDatePlus1DayForPreview;

        // Convert closingDate string to Date object and transform
        const closingDateObj = new Date(closingDate);
        const closingDateForPreview = this.dateLocalePipe.transform(closingDateObj).toLocaleString();

        let isReferenceAHiddenGroup = false;

        // used to identify each row incase 2 movements have same name
        this.rowNumber = 0;

        let numberFormat = '1.2-2';
        if (hideDecimalPlaces) {
            numberFormat = '1.0-0';
        }
        const valueDivisor = PdfTronMapperHelper.getValueDivisor(roundToThousands);
        const currencyLabel = PdfTronMapperHelper.getCurrencyLabel(currency, roundToThousands);

        const fixedAssetScheduleTableDataForPrint: FixedAssetScheduleTableDataForPrint = {
            t: '',
            rows: [],
        };

        const referenceFormattedRow: FixedAssetScheduleRowFormatted = {
            rowType: RowTypes.ROW,
            t: '',
            g1oV: '',
            g1a: '',
            g1d: '',
            g1t: '',
            g1cV: '',
            g2oV: '',
            g2a: '',
            g2d: '',
            g2t: '',
            g2cV: '',
            aV: '',
            cV: '',
            pV: '',
            level: Levels.LEVEL_1,
            markedForRemoval: true,
        };

        const referenceNameToFixedAssetsScheduleRowFormatedMap = new Map([
            [fixedAssetsSchedule.reference + '__' + 0, referenceFormattedRow],
        ]);

        let movementNameMapToFixedAssetsScheduleRowFormated = this.getMovementNameMapToFixedAssetsScheduleRowFormated(
            fixedAssetsSchedule.movements,
            numberFormat,
            valueDivisor,
            nonBreakingHyphen,
        );

        movementNameMapToFixedAssetsScheduleRowFormated = new Map([
            ...referenceNameToFixedAssetsScheduleRowFormatedMap,
            ...movementNameMapToFixedAssetsScheduleRowFormated,
        ]);

        if (fixedAssetsSchedule.referenceType === FixedAssetsScheduleReferenceType.GROUP) {
            const foundGroup = this.searchForReferenceGroup(fixedAssetsSchedule.reference, [mappingTemplate.group]);

            if (foundGroup == null) {
                throw new Error('no matching group found for reference group');
            }

            if (foundGroup.isHiddenGroup) {
                isReferenceAHiddenGroup = true;
            }

            this.setTextAndCvAndPvAndLevelForGroup(
                foundGroup,
                movementNameMapToFixedAssetsScheduleRowFormated,
                Levels.LEVEL_1,
                numberFormat,
                valueDivisor,
                hideEmptyLines,
                nonBreakingHyphen,
            );

            if (foundGroup.sortedItems == null) {
                throw new Error(`sortedItems array does not exist for group ${foundGroup.name}`);
            }

            for (const sortedItem of foundGroup.sortedItems) {
                if (isGroup(sortedItem as SortedMappingItem)) {
                    if (!(sortedItem as IAccountMappingTemplateGroup).isHiddenGroup) {
                        this.setTextAndCvAndPvAndLevelForGroup(
                            sortedItem as IAccountMappingTemplateGroup,
                            movementNameMapToFixedAssetsScheduleRowFormated,
                            Levels.LEVEL_2,
                            numberFormat,
                            valueDivisor,
                            hideEmptyLines,
                            nonBreakingHyphen,
                        );
                    }

                    this.setTextAndCvAndPvAndLevelForNestedGroups(
                        (sortedItem as IAccountMappingTemplateGroup).sortedItems as SortedMappingItem[],
                        movementNameMapToFixedAssetsScheduleRowFormated,
                        Levels.LEVEL_2,
                        numberFormat,
                        valueDivisor,
                        currency,
                        predefinedLabels,
                        hideEmptyLines,
                        nonBreakingHyphen,
                    );
                } else {
                    this.setTextAndCvAndPvAndLevelInsideReferenceGroupItem(
                        sortedItem as AccountMappingTemplateGroupItem,
                        movementNameMapToFixedAssetsScheduleRowFormated,
                        Levels.LEVEL_2,
                        numberFormat,
                        valueDivisor,
                        predefinedLabels,
                        hideEmptyLines,
                        nonBreakingHyphen,
                    );
                }
            }
        }

        if (fixedAssetsSchedule.referenceType === FixedAssetsScheduleReferenceType.FLI) {
            const foundFli = this.searchForReferenceFli(fixedAssetsSchedule.reference, [mappingTemplate.group]);
            if (foundFli == null) {
                throw new Error('no matching fli found for reference fli');
            }
            this.setTextAndCvAndPvAndLevelInsideFli(
                foundFli,
                movementNameMapToFixedAssetsScheduleRowFormated,
                Levels.LEVEL_1,
                numberFormat,
                valueDivisor,
                predefinedLabels,
                hideEmptyLines,
                nonBreakingHyphen,
            );
        }

        if (
            fixedAssetsSchedule.referenceType === FixedAssetsScheduleReferenceType.SUB_FLI ||
            fixedAssetsSchedule.referenceType === FixedAssetsScheduleReferenceType.OF_THAT_ITEM
        ) {
            const foundSubFli = this.searchForReferenceSubFli(fixedAssetsSchedule.reference, [mappingTemplate.group]);

            if (foundSubFli == null) {
                throw new Error('no matching fli found for reference fli');
            }
            let hideItem = false;
            if (
                hideEmptyLines &&
                foundSubFli.calculatedClosingBalance == 0 &&
                foundSubFli.calculatedOpeningBalance == 0
            ) {
                hideItem = true;
            }
            const referenceItemFormattedRow = movementNameMapToFixedAssetsScheduleRowFormated.get(
                foundSubFli.name + '__' + this.rowNumber,
            );
            if (!(hideEmptyLines && hideItem)) {
                referenceItemFormattedRow.markedForRemoval = false;
            }
            referenceItemFormattedRow.t = `A. ${foundSubFli.caption}`;
            referenceItemFormattedRow.cV = this.aumFinancialNumberPipe.transform(
                PdfTronMapperHelper.scaleValue(foundSubFli.calculatedClosingBalance, valueDivisor),
                numberFormat,
                nonBreakingHyphen,
            );
            referenceItemFormattedRow.pV = this.aumFinancialNumberPipe.transform(
                PdfTronMapperHelper.scaleValue(foundSubFli.calculatedOpeningBalance, valueDivisor),
                numberFormat,
                nonBreakingHyphen,
            );

            movementNameMapToFixedAssetsScheduleRowFormated.set(
                foundSubFli.name + '__' + this.rowNumber,
                referenceItemFormattedRow,
            );
            this.rowNumber++;
        }

        const movementScheduleRowsArray = Array.from(movementNameMapToFixedAssetsScheduleRowFormated.values()).filter(
            (value) => value != undefined,
        );
        if (isReferenceAHiddenGroup) {
            movementScheduleRowsArray.shift();
        }

        const rowsWithSubtotals = this.insertSummationRows(
            movementScheduleRowsArray,
            numberFormat,
            valueDivisor,
            predefinedLabels,
            nonBreakingHyphen,
        );
        fixedAssetScheduleTableDataForPrint.rows.push({
            rowType: RowTypes.TABLE_HEAD,
            t: '',
            g1oV:
                predefinedLabels.get('aumico_report_fas_print_section_costs_title') +
                ' ' +
                periodStartForPreview +
                '\n' +
                currencyLabel,
            g1a: predefinedLabels.get('aumico_report_fas_print_costs_additions') + '\n' + currencyLabel,
            g1d: predefinedLabels.get('aumico_report_fas_print_costs_disposals') + '\n' + currencyLabel,
            g1t: predefinedLabels.get('aumico_report_fas_print_costs_transfers') + '\n' + currencyLabel,
            g1cV:
                predefinedLabels.get('aumico_report_fas_print_section_costs_title') +
                ' ' +
                closingDateForPreview +
                '\n' +
                currencyLabel,
            g2oV:
                predefinedLabels.get('aumico_report_fas_print_section_depr_title') +
                ' ' +
                periodStartForPreview +
                '\n' +
                currencyLabel,
            g2a: predefinedLabels.get('aumico_report_fas_print_depreciations_additions') + '\n' + currencyLabel,
            g2d: predefinedLabels.get('aumico_report_fas_print_depreciations_disposals') + '\n' + currencyLabel,
            g2t: predefinedLabels.get('aumico_report_fas_print_depreciations_transfers') + '\n' + currencyLabel,
            g2cV:
                predefinedLabels.get('aumico_report_fas_print_section_depr_title') +
                ' ' +
                closingDateForPreview +
                '\n' +
                currencyLabel,
            aV: predefinedLabels.get('aumico_report_fas_print_section_valueApr_title') + '\n' + currencyLabel,
            cV:
                predefinedLabels.get('aumico_report_fas_print_section_bookvalue_current') +
                ' ' +
                closingDateForPreview +
                '\n' +
                currencyLabel,
            pV: noPreviousData
                ? ''
                : predefinedLabels.get('aumico_report_fas_print_section_bookvalue_previous') +
                  ' ' +
                  openingDateForPreview +
                  '\n' +
                  currencyLabel,
            level: Levels.LEVEL_1,
            markedForRemoval: false,
        });

        //Remove Values of Parent
        const cleanedRows = this.removeTotalsInParent(rowsWithSubtotals);
        const withoutEmptyLines = this.removeMarkedToRemove(cleanedRows);

        fixedAssetScheduleTableDataForPrint.rows.push(...withoutEmptyLines);
        return fixedAssetScheduleTableDataForPrint;
    }

    private removeTotalsInParent(data: FixedAssetScheduleRowFormatted[]): FixedAssetScheduleRowFormatted[] {
        for (let i = 0; i < data.length - 1; i++) {
            const nextLevel = data[i + 1].level;

            // Check if the next level is not null and higher than the current level
            if (nextLevel !== null && nextLevel > data[i].level) {
                data[i] = {
                    rowType: data[i].rowType,
                    t: data[i].t,
                    level: data[i].level,
                    markedForRemoval: data[i].markedForRemoval,
                    // Setting other properties to empty strings
                    g1oV: '',
                    g1a: '',
                    g1d: '',
                    g1t: '',
                    g1cV: '',
                    g2oV: '',
                    g2a: '',
                    g2d: '',
                    g2t: '',
                    g2cV: '',
                    aV: '',
                    cV: '',
                    pV: '',
                    disableNumbering: data[i].disableNumbering,
                    isGroup: data[i].isGroup,
                };
            }
        }
        return data;
    }
    private removeMarkedToRemove(data: FixedAssetScheduleRowFormatted[]): FixedAssetScheduleRowFormatted[] {
        return data.filter((item: FixedAssetScheduleRowFormatted) => item.markedForRemoval === false);
    }

    private setTextAndCvAndPvAndLevelForNestedGroups(
        sortedItems: SortedMappingItem[],
        movementNameMapToFixedAssetsScheduleRowFormated: Map<string, FixedAssetScheduleRowFormatted>,
        hierarchyLevel: Levels,
        numberFormat: string,
        valueDivisor: number,
        currency: string,
        predefinedLabels: Map<string, string>,
        hideEmptyLines: boolean,
        nonBreakingHyphen: boolean,
    ): void {
        sortedItems.forEach((sortedItem: SortedMappingItem) => {
            if (isGroup(sortedItem)) {
                if (!sortedItem.isHiddenGroup) {
                    this.setTextAndCvAndPvAndLevelForGroup(
                        sortedItem,
                        movementNameMapToFixedAssetsScheduleRowFormated,
                        hierarchyLevel,
                        numberFormat,
                        valueDivisor,
                        hideEmptyLines,
                        nonBreakingHyphen,
                    );
                }

                this.setTextAndCvAndPvAndLevelForNestedGroups(
                    sortedItem.sortedItems as SortedMappingItem[],
                    movementNameMapToFixedAssetsScheduleRowFormated,
                    hierarchyLevel + 1,
                    numberFormat,
                    valueDivisor,
                    currency,
                    predefinedLabels,
                    hideEmptyLines,
                    nonBreakingHyphen,
                );
            } else {
                this.setTextAndCvAndPvAndLevelInsideReferenceGroupItem(
                    sortedItem as AccountMappingTemplateGroupItem,
                    movementNameMapToFixedAssetsScheduleRowFormated,
                    hierarchyLevel + 1,
                    numberFormat,
                    valueDivisor,
                    predefinedLabels,
                    hideEmptyLines,
                    nonBreakingHyphen,
                );
            }
        });
    }

    private setTextAndCvAndPvAndLevelForGroup(
        group: IAccountMappingTemplateGroup,
        movementNameMapToFixedAssetsScheduleRowFormated: Map<string, FixedAssetScheduleRowFormatted>,
        hierarchyLevel: Levels,
        numberFormat: string,
        valueDivisor: number,
        hideEmptyLines: boolean,
        nonBreakingHyphen: boolean,
    ): void {
        const name = group.name + '__' + this.rowNumber;
        let hideItem = false;
        if (hideEmptyLines && group.calculatedClosingBalance == 0 && group.calculatedOpeningBalance == 0) {
            hideItem = true;
        }
        const referenceGroupFormattedRow = movementNameMapToFixedAssetsScheduleRowFormated.get(name);
        if (!(hideEmptyLines && hideItem)) {
            referenceGroupFormattedRow.markedForRemoval = false;
        }
        referenceGroupFormattedRow.t = group.title;
        referenceGroupFormattedRow.cV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(group.calculatedClosingBalance, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        referenceGroupFormattedRow.pV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(group.calculatedOpeningBalance, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        referenceGroupFormattedRow.level = hierarchyLevel;

        referenceGroupFormattedRow.disableNumbering = group.disableNumbering;

        referenceGroupFormattedRow.isGroup = true;

        movementNameMapToFixedAssetsScheduleRowFormated.set(
            group.name + '__' + this.rowNumber,
            referenceGroupFormattedRow,
        );
        this.rowNumber++;
    }

    private getMovementNameMapToFixedAssetsScheduleRowFormated(
        fixedAssetsScheduleMovements: FixedAssetsScheduleMovement[],
        numberFormat: string,
        valueDivisor: number,
        nonBreakingHyphen: boolean,
    ): Map<string, FixedAssetScheduleRowFormatted> {
        const movementNameMapToFixedAssetsScheduleRowFormated = new Map<string, FixedAssetScheduleRowFormatted>();
        let index = 1;
        for (const movement of fixedAssetsScheduleMovements) {
            const formattedRow: FixedAssetScheduleRowFormatted = {
                rowType: RowTypes.ROW,
                t: '',
                g1oV: this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(movement.costs.openingValue, valueDivisor),
                    numberFormat,
                    nonBreakingHyphen,
                ),
                g1a:
                    movement.costs.additions != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.costs.additions, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g1d:
                    movement.costs.disposals != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.costs.disposals, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g1t:
                    movement.costs.transfers != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.costs.transfers, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g1cV: this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(movement.costs.closingValue, valueDivisor),
                    numberFormat,
                    nonBreakingHyphen,
                ),
                g2oV:
                    movement.depreciations != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.depreciations.openingValue, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g2a:
                    movement.depreciations != null && movement.depreciations.additions != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.depreciations.additions, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g2d:
                    movement.depreciations != null && movement.depreciations.disposals != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.depreciations.disposals, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g2t:
                    movement.depreciations != null && movement.depreciations.transfers != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.depreciations.transfers, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                g2cV:
                    movement.depreciations != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.depreciations.closingValue, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                aV:
                    movement.valueAppreciation != null
                        ? this.aumFinancialNumberPipe.transform(
                              PdfTronMapperHelper.scaleValue(movement.valueAppreciation, valueDivisor),
                              numberFormat,
                              nonBreakingHyphen,
                          )
                        : '',
                cV: '',
                pV: '',
                // Adding all with level 1 as initial value
                level: Levels.LEVEL_1,
                markedForRemoval: true,
            };

            // adding index to map key to make each key unique even if 2 movements have the same name (fli and group for example)
            movementNameMapToFixedAssetsScheduleRowFormated.set(movement.name + '__' + index, formattedRow);
            index = index + 1;
        }

        return movementNameMapToFixedAssetsScheduleRowFormated;
    }

    private searchForReferenceGroup(
        reference: string,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroup | undefined {
        for (let group of templateGroups) {
            if (group.name === reference) {
                return group;
            }

            let nestedGroup = this.searchForReferenceGroup(reference, group.groups);
            if (nestedGroup != null) {
                return nestedGroup;
            }
        }
        return undefined;
    }

    private searchForReferenceFli(
        reference: string,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroupItemFLI | undefined {
        for (let group of templateGroups) {
            // First, check the items at the current group level
            for (let item of group.items) {
                if (item.name === reference && !isSubTotal(item)) {
                    return item as IAccountMappingTemplateGroupItemFLI;
                }
            }

            // If not found at current level, search within nested groups
            let nestedItem = this.searchForReferenceFli(reference, group.groups);
            if (nestedItem) {
                return nestedItem;
            }
        }
        return null;
    }

    private searchForReferenceSubFli(
        reference: string,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingSubFLI | undefined {
        for (let group of templateGroups) {
            // Iterate over items
            for (let item of group.items) {
                for (let account of (item as IAccountMappingTemplateGroupItemFLI).accounts) {
                    if (
                        account.type === ItemType.SUB_FLI ||
                        (account.type === ItemType.OF_THAT_ITEM && account.name === reference)
                    ) {
                        return account;
                    }
                }
            }

            // If not found at current level, search within nested groups
            let nestedSubFLI = this.searchForReferenceSubFli(reference, group.groups);
            if (nestedSubFLI) {
                return nestedSubFLI;
            }
        }
        return undefined;
    }

    private setTextAndCvAndPvAndLevelInsideReferenceGroupItem(
        sortedItem: AccountMappingTemplateGroupItem,
        movementNameMapToFixedAssetsScheduleRowFormated: Map<string, FixedAssetScheduleRowFormatted>,
        hierarchyLevel: Levels,
        numberFormat: string,
        valueDivisor: number,
        predefinedLabels: Map<string, string>,
        hideEmptyLines: boolean,
        nonBreakingHyphen: boolean,
    ): void {
        if (isFli(sortedItem)) {
            this.setTextAndCvAndPvAndLevelInsideFli(
                sortedItem as IAccountMappingTemplateGroupItemFLI,
                movementNameMapToFixedAssetsScheduleRowFormated,
                hierarchyLevel,
                numberFormat,
                valueDivisor,
                predefinedLabels,
                hideEmptyLines,
                nonBreakingHyphen,
            );
        }
    }

    private setTextAndCvAndPvAndLevelInsideFli(
        fli: IAccountMappingTemplateGroupItemFLI,
        movementNameMapToFixedAssetsScheduleRowFormated: Map<string, FixedAssetScheduleRowFormatted>,
        hierarchyLevel: Levels,
        numberFormat: string,
        valueDivisor: number,
        predefinedLabels: Map<string, string>,
        hideEmptyLines: boolean,
        nonBreakingHyphen: boolean,
    ): void {
        const name = fli.name + '__' + this.rowNumber;
        let hideItem = false;
        if (hideEmptyLines && fli.calculatedClosingBalance == 0 && fli.calculatedOpeningBalance == 0) {
            hideItem = true;
        }

        const fliFormattedRow = movementNameMapToFixedAssetsScheduleRowFormated.get(name);
        if (!(hideEmptyLines && hideItem)) {
            fliFormattedRow.markedForRemoval = false;
        }
        fliFormattedRow.t = fli.caption;
        fliFormattedRow.cV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(fli.calculatedClosingBalance, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        fliFormattedRow.pV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(fli.calculatedOpeningBalance, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        fliFormattedRow.level = hierarchyLevel;
        fliFormattedRow.disableNumbering = fli.disableNumbering;

        movementNameMapToFixedAssetsScheduleRowFormated.set(fli.name + '__' + this.rowNumber, fliFormattedRow);

        this.rowNumber++;

        // check existance of a direct assigned grouped movements entry
        const directAssignedAccounts = fli.accounts.filter(
            (acc) => acc.type !== ItemType.SUB_FLI && acc.type !== ItemType.OF_THAT_ITEM,
        );
        const subflis = fli.accounts.filter(
            (acc) => acc.type === ItemType.SUB_FLI || acc.type === ItemType.OF_THAT_ITEM,
        );
        if (
            directAssignedAccounts != null &&
            directAssignedAccounts.length !== EMPTY_ARRAY_LENGTH &&
            subflis != null &&
            subflis.length !== EMPTY_ARRAY_LENGTH
        ) {
            this.setTextAndCvAndPvAndLevelForDirectlyGroupedMovements(
                directAssignedAccounts as IAccount[],
                movementNameMapToFixedAssetsScheduleRowFormated,
                hierarchyLevel + 1,
                numberFormat,
                valueDivisor,
                predefinedLabels,
                hideEmptyLines,
                nonBreakingHyphen,
            );
        }
        subflis.forEach((subfli: IAccountMappingSubFLI) => {
            this.setTextAndCvAndPvAndLevelInsideSubFli(
                subfli,
                movementNameMapToFixedAssetsScheduleRowFormated,
                hierarchyLevel + 1,
                numberFormat,
                valueDivisor,
                hideEmptyLines,
                nonBreakingHyphen,
            );
        });
    }

    private setTextAndCvAndPvAndLevelInsideSubFli(
        subfli: IAccountMappingSubFLI,
        movementNameMapToFixedAssetsScheduleRowFormated: Map<string, FixedAssetScheduleRowFormatted>,
        hierarchyLevel: Levels,
        numberFormat: string,
        valueDivisor: number,
        hideEmptyLines: boolean,
        nonBreakingHyphen: boolean,
    ): void {
        const name = subfli.name + '__' + this.rowNumber;

        let hideItem = false;
        if (hideEmptyLines && subfli.calculatedClosingBalance == 0 && subfli.calculatedOpeningBalance == 0) {
            hideItem = true;
        }
        const subfliFormattedRow = movementNameMapToFixedAssetsScheduleRowFormated.get(name);
        if (!(hideEmptyLines && hideItem)) {
            subfliFormattedRow.markedForRemoval = false;
        }
        subfliFormattedRow.t = subfli.caption;
        subfliFormattedRow.cV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(subfli.calculatedClosingBalance, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        subfliFormattedRow.pV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(subfli.calculatedOpeningBalance, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        subfliFormattedRow.level = hierarchyLevel;
        subfliFormattedRow.disableNumbering = subfli.disableNumbering;

        movementNameMapToFixedAssetsScheduleRowFormated.set(subfli.name + '__' + this.rowNumber, subfliFormattedRow);
        this.rowNumber++;
    }

    private setTextAndCvAndPvAndLevelForDirectlyGroupedMovements(
        accounts: IAccount[],
        movementNameMapToFixedAssetsScheduleRowFormated: Map<string, FixedAssetScheduleRowFormatted>,
        hierarchyLevel: Levels,
        numberFormat: string,
        valueDivisor: number,
        predefinedLabels: Map<string, string>,
        hideEmptyLines: boolean,
        nonBreakingHyphen: boolean,
    ): void {
        const accountsOpeningSum = NumberUtils.sumIgnoringInfiniteNumbers(
            accounts.map((acc) => acc.calculatedOpeningBalance),
            0,
        );
        const accountsClosingSum = NumberUtils.sumIgnoringInfiniteNumbers(
            accounts.map((acc) => acc.calculatedClosingBalance),
            0,
        );
        const directAssignedGroupedMovementsFormattedRow = movementNameMapToFixedAssetsScheduleRowFormated.get(
            DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME + '__' + this.rowNumber,
        );

        if (directAssignedGroupedMovementsFormattedRow == null) {
            return;
        }

        const caption = predefinedLabels.get('aumico_annual_report_fas_direct_assigned_accounts');

        directAssignedGroupedMovementsFormattedRow.t = caption;
        directAssignedGroupedMovementsFormattedRow.cV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(accountsClosingSum, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        directAssignedGroupedMovementsFormattedRow.pV = this.aumFinancialNumberPipe.transform(
            PdfTronMapperHelper.scaleValue(accountsOpeningSum, valueDivisor),
            numberFormat,
            nonBreakingHyphen,
        );
        directAssignedGroupedMovementsFormattedRow.level = hierarchyLevel;

        movementNameMapToFixedAssetsScheduleRowFormated.set(
            DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME + '__' + this.rowNumber,
            directAssignedGroupedMovementsFormattedRow,
        );
        this.rowNumber++;
    }

    private getSubtotalInfo(rows: FixedAssetScheduleRowFormatted[], targetLevel: number, index: number): SubtotalInfo {
        let potentialParentRows = rows.filter((parentRow) => parentRow.level === targetLevel);
        let info: SubtotalInfo = { name: '', isMarkedForRemoval: true }; // Default values

        if (potentialParentRows != null && potentialParentRows.length === 1) {
            info.name = potentialParentRows[0].t || EMPTY_STRING; // Assuming FIRST_ELEMENT_INDEX is 0
            info.isMarkedForRemoval = potentialParentRows[0].markedForRemoval;
            return info;
        }

        if (potentialParentRows != null && potentialParentRows.length > 1) {
            let nearestIndex = -1; // Start with -1 to indicate not found

            potentialParentRows.forEach((obj) => {
                const currentIndex = rows.indexOf(obj);
                if (currentIndex < index && currentIndex > nearestIndex) {
                    nearestIndex = currentIndex;
                }
            });

            if (nearestIndex !== -1) {
                // Found a valid index
                info.name = rows[nearestIndex].t || EMPTY_STRING;
                info.isMarkedForRemoval = rows[nearestIndex].markedForRemoval;
            }
        }

        return info;
    }

    private addCurrentRowValuesToSubtotal(
        row: FixedAssetScheduleRowFormatted,
        subtotal: FixedAssetScheduleRowFormatted,
        numberFormat: string,
        valueDivisor: number,
        nonBreakingHyphen: boolean,
    ): FixedAssetScheduleRowFormatted {
        // Add the current row values to the current level subtotal
        ['g1oV', 'g1a', 'g1d', 'g1t', 'g1cV', 'g2oV', 'g2a', 'g2d', 'g2t', 'g2cV', 'aV', 'cV', 'pV'].forEach(
            (field) => {
                const currentLevelSubtotalNumber = this.aumFinancialNumberPipe.parseFormattedNumber(
                    subtotal[field],
                    nonBreakingHyphen,
                );
                const currentPropertyValue = this.aumFinancialNumberPipe.parseFormattedNumber(
                    row[field],
                    nonBreakingHyphen,
                );
                const newSubtotal = NumberUtils.sumIgnoringInfiniteNumbers(
                    [currentLevelSubtotalNumber, currentPropertyValue],
                    0,
                );

                // The operands were parsed from already scaled cells, so the sum is in the print unit.
                subtotal[field] = this.aumFinancialNumberPipe.transform(
                    newSubtotal,
                    numberFormat,
                    nonBreakingHyphen,
                );
            },
        );
        return subtotal;
    }

    private getInitialSubtotalEmptyEntry(
        subtotalName: string,
        level: number,
        predefinedLabels: Map<string, string>,
        markedForRemoval: boolean,
    ): FixedAssetScheduleRowFormatted {
        return {
            rowType: RowTypes.GROUP_TOTAL,
            t: predefinedLabels.get('aumico_report_fas_print_subtotal') + ' ' + subtotalName,

            g1oV: '0',
            g1a: '0',
            g1d: '0',
            g1t: '0',
            g1cV: '0',
            g2oV: '0',
            g2a: '0',
            g2d: '0',
            g2t: '0',
            g2cV: '0',
            aV: '0',
            cV: '0',
            pV: '0',
            level: level,
            markedForRemoval: markedForRemoval,
        };
    }

    private insertSummationRows(
        rows: FixedAssetScheduleRowFormatted[],
        numberFormat: string,
        valueDivisor: number,
        predifinedLables: Map<string, string>,
        nonBreakingHyphen: boolean,
    ): FixedAssetScheduleRowFormatted[] {
        const subtotals: FixedAssetScheduleRowFormatted[] = [];
        const rowsWithSubtotals: FixedAssetScheduleRowFormatted[] = [];
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];

            let subTotalInfo: SubtotalInfo;
            let isParentHiddenGroup: boolean;

            if (rows[i - 1] != null && row.level > rows[i - 1].level + 1) {
                // if difference between current level and previous level is more than 1 , then parent is a hidden group so subtotal is for hidden group
                isParentHiddenGroup = true;

                subTotalInfo = this.getSubtotalInfo(rows, row.level - 2, i);
            } else {
                subTotalInfo = this.getSubtotalInfo(rows, row.level - 1, i);
            }

            if (isParentHiddenGroup) {
                // start adding subtotal from this level to include the calculation of this hidden group
                const hiddenParentLevelSubtotal: FixedAssetScheduleRowFormatted =
                    subtotals[row.level - 1] ||
                    this.getInitialSubtotalEmptyEntry(
                        subTotalInfo.name,
                        row.level - 2,
                        predifinedLables,
                        subTotalInfo.isMarkedForRemoval,
                    );

                subtotals[row.level - 1] = this.addCurrentRowValuesToSubtotal(
                    row,
                    hiddenParentLevelSubtotal,
                    numberFormat,
                    valueDivisor,
                    nonBreakingHyphen,
                );
            }

            // we reset subtotal of a certain level incase it is repeated again in a completely different parent
            // we know this by comparing if the current level is bigger than the previous level
            const currentLevelSubtotal: FixedAssetScheduleRowFormatted =
                (rows[i - 1] != null && row.level > rows[i - 1].level) || subtotals[row.level] == null
                    ? this.getInitialSubtotalEmptyEntry(
                          subTotalInfo.name,
                          row.level - 1,
                          predifinedLables,
                          subTotalInfo.isMarkedForRemoval,
                      )
                    : subtotals[row.level];

            subtotals[row.level] = this.addCurrentRowValuesToSubtotal(
                row,
                currentLevelSubtotal,
                numberFormat,
                valueDivisor,
                nonBreakingHyphen,
            );
            rowsWithSubtotals.push(row);

            //pushing subtotals in the correct position
            if (i === rows.length - 1 || rows[i + 1].level < row.level) {
                for (let j = row.level; j < subtotals.length; j++) {
                    if (subtotals[j]) {
                        const subtotalRow = subtotals[j];

                        rowsWithSubtotals.push(subtotalRow);
                        delete subtotals[j];
                    }
                }
            }
        }

        return rowsWithSubtotals;
    }
}
